/**
 * Notification Sound Service
 * Handles playing notification sounds for mentions and alerts
 */

// Create audio context for notification sounds
let audioContext = null;
let notificationBuffer = null;

// Initialize audio context (must be called after user interaction)
const initAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

// Generate a pleasant notification tone programmatically
const generateNotificationTone = () => {
  const ctx = initAudioContext();
  const duration = 0.3;
  const sampleRate = ctx.sampleRate;
  const buffer = ctx.createBuffer(1, duration * sampleRate, sampleRate);
  const data = buffer.getChannelData(0);
  
  // Create a pleasant two-tone notification sound
  for (let i = 0; i < buffer.length; i++) {
    const t = i / sampleRate;
    // First tone (higher pitch)
    const freq1 = 880; // A5
    const freq2 = 1100; // C#6
    
    // Envelope (fade in/out)
    let envelope = 1;
    if (t < 0.05) envelope = t / 0.05;
    if (t > 0.25) envelope = (0.3 - t) / 0.05;
    
    // Mix two frequencies for a pleasant sound
    if (t < 0.15) {
      data[i] = Math.sin(2 * Math.PI * freq1 * t) * envelope * 0.3;
    } else {
      data[i] = Math.sin(2 * Math.PI * freq2 * t) * envelope * 0.3;
    }
  }
  
  return buffer;
};

// Play notification sound
export const playNotificationSound = () => {
  // Check if sounds are enabled
  const soundEnabled = localStorage.getItem('notification_sound_enabled');
  if (soundEnabled === 'false') {
    return;
  }
  
  try {
    const ctx = initAudioContext();
    
    // Resume audio context if suspended (browser autoplay policy)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    
    // Generate tone if not cached
    if (!notificationBuffer) {
      notificationBuffer = generateNotificationTone();
    }
    
    // Create and play the sound
    const source = ctx.createBufferSource();
    source.buffer = notificationBuffer;
    
    // Add gain node for volume control
    const gainNode = ctx.createGain();
    const volume = parseFloat(localStorage.getItem('notification_volume') || '0.5');
    gainNode.gain.value = volume;
    
    source.connect(gainNode);
    gainNode.connect(ctx.destination);
    source.start();
  } catch (error) {
    console.warn('Could not play notification sound:', error);
  }
};

// Check if notification sound is enabled
export const isNotificationSoundEnabled = () => {
  const enabled = localStorage.getItem('notification_sound_enabled');
  return enabled !== 'false'; // Default to true
};

// Toggle notification sound
export const setNotificationSoundEnabled = (enabled) => {
  localStorage.setItem('notification_sound_enabled', enabled.toString());
};

// Get notification volume
export const getNotificationVolume = () => {
  return parseFloat(localStorage.getItem('notification_volume') || '0.5');
};

// Set notification volume (0-1)
export const setNotificationVolume = (volume) => {
  localStorage.setItem('notification_volume', Math.max(0, Math.min(1, volume)).toString());
};

// Initialize on first user interaction
export const initNotificationSound = () => {
  const handleFirstInteraction = () => {
    initAudioContext();
    document.removeEventListener('click', handleFirstInteraction);
    document.removeEventListener('keydown', handleFirstInteraction);
  };
  
  document.addEventListener('click', handleFirstInteraction, { once: true });
  document.addEventListener('keydown', handleFirstInteraction, { once: true });
};

export default {
  playNotificationSound,
  isNotificationSoundEnabled,
  setNotificationSoundEnabled,
  getNotificationVolume,
  setNotificationVolume,
  initNotificationSound
};
